import { watch, ref } from 'vue';
import { useModal } from 'vue-final-modal';
import { storeToRefs } from 'pinia';

import { ConfirmCloseModal, SuccessModal } from '@/shared/ui';
import { useFileUploadStore } from '@/features/Document';
import type { FileUploadCallbacks } from './upload/types';
import { useDocumentRefreshStore } from '@/entities/Document';

export default function useDocumentUploadModal<
  T extends Record<string, (...args: any[]) => void> | ((...args: any[]) => void),
>(
  emit: T,
  getReplacementContext?: () => { documentId: number; revisionId: number } | undefined,
  getUrlData?: () => Array<{
    url: string;
    name: string;
    changelog?: string;
    managerId?: number;
  }>,
  isExternal?: () => boolean,
  getExternalDocumentName?: () => string
) {
  const fileUploadStore = useFileUploadStore();
  const { hasFiles, filesLoaded, isUrlUploadValid, uploadedDocumentIds, isUploading } =
    storeToRefs(fileUploadStore);

  const allChangelogsFilled = ref(false);

  const handleFileUpload = (newFiles: File[], isExternal?: boolean, documentName?: string) => {
    fileUploadStore.handleFileUpload(newFiles, isExternal, documentName);
  };

  const handleFileProgress = (fileId: string, progress: number) => {
    fileUploadStore.handleFileProgress(fileId, progress);
  };

  const handleFileComplete = (documentIds?: number[]) => {
    fileUploadStore.handleFileComplete(documentIds);
  };

  const handleFilesSelectedWithContext = (
    newFiles: FileList | null,
    onFilesSelected: FileUploadCallbacks,
    isExternal?: boolean,
    documentName?: string
  ) => {
    fileUploadStore.handleFilesSelected(newFiles, onFilesSelected, isExternal, documentName);
  };

  const { triggerDocumentRefresh } = useDocumentRefreshStore();

  watch(hasFiles, (newVal, oldVal) => {
    if (!newVal && oldVal) {
      filesLoaded.value = false;
    }
  });

  const emitEvent = (eventName: string, ...args: any[]) => {
    if (typeof emit === 'function') {
      emit(eventName, ...args);
    } else if (typeof emit === 'object' && emit[eventName]) {
      emit[eventName](...args);
    }
  };

  const { open: openConfirmation, close: closeConfirmation } = useModal({
    component: ConfirmCloseModal,
    attrs: {
      // @ts-ignore
      clickToClose: false,
      escToClose: false,
      title: 'Tem certeza que deseja fechar o envio de documentos?',
      description:
        'Fechar cancela todas as ações realizadas na página. A ação não pode ser desfeita.',
      onClose() {
        closeConfirmation();
      },
      onConfirm() {
        closeConfirmation();
        emitEvent('close', fileUploadStore.uploadedDocumentIds);
      },
    },
  });

  const { open: openSuccessModal, close: closeSuccessModal } = useModal({
    component: SuccessModal,
    attrs: {
      // @ts-ignore
      clickToClose: false,
      escToClose: false,
      title: 'Documentos enviados com sucesso!',
      description: 'Os documentos passarão pelo processo de homologação.',
      async onClose() {
        closeSuccessModal();
        triggerDocumentRefresh();
        emitEvent('upload-success');
        emitEvent('close');
      },
    },
  });

  const handleClose = () => {
    if (hasFiles.value || isUrlUploadValid.value) {
      openConfirmation();
    } else {
      emitEvent('close');
    }
  };

  const handleConfirm = async () => {
    const replacementContext = getReplacementContext?.();
    
    const urlData = getUrlData?.();
    if (urlData && urlData.length > 0) {
      try {
        const external = isExternal?.() ?? false;
        const documentName = getExternalDocumentName?.();
        const documentIds = await fileUploadStore.linkUrls(urlData, external, documentName);
        if (documentIds.length > 0) {
          openSuccessModal();
        }
      } catch (error) {
        console.error('Link URLs failed:', error);
      }
      return;
    }

    try {
      await fileUploadStore.uploadSelectedFiles(undefined, replacementContext);
      if (hasFiles.value) {
        openSuccessModal();
      }
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleAllFilesDeleted = () => {
    hasFiles.value = false;
    filesLoaded.value = false;
    allChangelogsFilled.value = false;
  };

  const handleChangelogValidation = (filled: boolean) => {
    allChangelogsFilled.value = filled;
  };

  return {
    hasFiles,
    isUrlUploadValid,
    filesLoaded,
    uploadedDocumentIds,
    isUploading,
    allChangelogsFilled,
    handleFileUpload,
    handleFileProgress,
    handleFileComplete,
    handleClose,
    handleConfirm,
    handleAllFilesDeleted,
    handleChangelogValidation,
    handleFilesSelected: handleFilesSelectedWithContext,
    cleanState: fileUploadStore.cleanState,
  };
}
